import { ButtonPrimary, Form, InputText } from '@lambdacurry/component-library';
import classNames from 'classnames';
import { FormikHelpers } from 'formik';
import { navigate } from 'gatsby';
import { StaticImage } from 'gatsby-plugin-image';
import { FC, useState } from 'react';
import * as Yup from 'yup';
import { Card, CardContent, Container, Grid, Layout } from 'lib/components';
import { useForgotPasswordMutation } from 'lib/home/generated/queries';
import { emailAddressValidation } from 'lib/forms/validation';
import { default as ForgotPasswordPattern } from '../../assets/forgot-password-pattern.svg';
import { BackButton, GettingStartedNoCookingFooter, Logo } from '../../components';

const initialValues = { email: '' };
const validationSchema = Yup.object(emailAddressValidation);
type FormValues = typeof initialValues;

const ForgotPasswordPage: FC = () => {
  const [emailSent, setEmailSent] = useState(false);
  const { mutate } = useForgotPasswordMutation({
    onSuccess: () => {
      setEmailSent(true);
    }
  });

  const handleSubmit = ({ email }: FormValues, formikHelpers: FormikHelpers<FormValues>) => {
    mutate({ forgotPasswordInput: { email } });
  };

  return (
    <div className="min-h-screen flex flex-col justify-between">
      <Layout title="Reset Your Password" className={classNames('forgot-password pt-8')}>
        <header className={classNames('h-16 mb-4 py-2 sm:py-12 flex justify-center items-center relative')}>
          <Logo />
        </header>

        <Container>
          <Grid>
            <div className="col-span-12 lg:col-span-10 lg:col-start-2">
              <Card className="relative">
                <div className="md:flex">
                  <CardContent className="flex flex-col justify-center my-8 md:my-0">
                    <BackButton
                      className="!relative !left-0 !top-0"
                      onClick={() => navigate('/')}
                      aria-label="Go to previous step"
                    />

                    <Form<FormValues>
                      initialValues={initialValues}
                      validationSchema={validationSchema}
                      onSubmit={handleSubmit}
                    >
                      {formikProps => (
                        <>
                          {!emailSent && (
                            <>
                              <h3>Reset Your Password</h3>
                              <InputText name="email" label="Email" />
                              <ButtonPrimary className="w-48 mt-6" type="submit">
                                Submit
                              </ButtonPrimary>
                            </>
                          )}

                          {emailSent && (
                            <>
                              <h3>Email Sent</h3>
                              <p>
                                A link to reset your password has been sent to{' '}
                                <strong>{formikProps.values.email}</strong>.
                              </p>
                              <ButtonPrimary type="submit">Re-send Link</ButtonPrimary>
                            </>
                          )}
                        </>
                      )}
                    </Form>
                  </CardContent>

                  <CardContent className="bg-brand-yellow-100 !border-t-0 md:max-w-md !pb-0 !pt-3 text-center">
                    <StaticImage
                      className="mx-auto"
                      src="../../assets/forgot-password-graphic.png"
                      alt="woman holding herbs"
                      placeholder="tracedSVG"
                      height={380}
                      quality={100}
                    />
                  </CardContent>
                </div>

                <ForgotPasswordPattern className="lg:w-full lg:h-auto bg-brand-green-50" />
              </Card>
            </div>
          </Grid>
        </Container>
      </Layout>

      <GettingStartedNoCookingFooter />
    </div>
  );
};

export default ForgotPasswordPage;
